/**
 * Side-by-Side Scores Matrix
 * Main component that renders desktop table or mobile cards based on screen size
 */

import React, { useState, useEffect } from 'react';
import type { CompositeScore } from '../../../hooks/useComparisonScores';
import { SideBySideMatrixDesktop } from './SideBySideMatrixDesktop';
import { SideBySideMatrixMobile } from './SideBySideMatrixMobile';

interface SideBySideMatrixProps {
  scores: CompositeScore[];
  responses: any[]; // Array of Response objects
  onOpenResponse: (responseId: string) => void;
  showHeader?: boolean; // Optional: show/hide header section (default: true)
}

export const SideBySideMatrix: React.FC<SideBySideMatrixProps> = ({
  scores,
  responses,
  onOpenResponse,
  showHeader = true
}) => {
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Helper to map modelId to responseId
  const handleOpenResponse = (modelId: string) => {
    const response = responses.find(r => r.modelId === modelId);
    if (response) {
      onOpenResponse(response.id);
    }
  };

  // Handle empty state
  if (!scores || scores.length === 0) {
    return (
      <div className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-xl p-8 text-center">
        <div className="w-16 h-16 bg-slate-700/50 rounded-lg flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-white mb-2">
          No Scores Available
        </h2>
        <p className="text-slate-400 text-sm">
          Score these responses to see a detailed comparison matrix.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      {showHeader && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-2">
            Scores Comparison Matrix
          </h3>
          <p className="text-slate-500 text-sm">
            Compare model performance across all criteria. Click any row to view the full response.
          </p>
        </div>
      )}

      {/* Matrix Content */}
      <div className="bg-slate-900/50 rounded-lg border border-slate-700/50 overflow-hidden">
        {isMobile ? (
          <div className="p-4">
            <SideBySideMatrixMobile
              scores={scores}
              onOpenResponse={handleOpenResponse}
            />
          </div>
        ) : (
          <SideBySideMatrixDesktop
            scores={scores}
            onOpenResponse={handleOpenResponse}
          />
        )}
      </div>

      {/* Footer Legend */}
      <div className="mt-4 flex items-center justify-between text-xs text-slate-500 flex-wrap gap-3">
        <div className="flex items-center gap-4 flex-wrap">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 bg-emerald-400 rounded-full"></span>
            Excellent (9.0+)
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 bg-green-400 rounded-full"></span>
            Good (8.0-8.9)
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 bg-yellow-400 rounded-full"></span>
            Fair (7.0-7.9)
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
            Poor (6.0-6.9)
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 bg-red-400 rounded-full"></span>
            Very Poor (&lt;6.0)
          </span>
        </div>
        <span className="text-slate-600">
          {scores.length} {scores.length === 1 ? 'model' : 'models'} evaluated
        </span>
      </div>
    </div>
  );
};
